// src/PhotoCropper.tsx
import React, { useState, useRef } from 'react';

interface PhotoCropperProps {
  countryCode?: 'INDIA' | 'MALAYSIA' | 'VIETNAM' | string;
}

export const PhotoCropper: React.FC<PhotoCropperProps> = ({ countryCode = 'INDIA' }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const country = countryCode.toUpperCase();

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeKB = Math.round(file.size / 1024);
    setFileInfo(`Original File: ${file.name} (${sizeKB} KB)`);

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(event.target?.result as string);
      setCroppedImage(null);
    };
    reader.readAsDataURL(file);
  };

  const handleCropImage = () => {
    if (!selectedImage) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current || document.createElement('canvas');

      let targetWidth = 350;
      let targetHeight = 350;

      if (country === 'VIETNAM') {
        targetWidth = 400;
        targetHeight = 600; // 4:6 aspect ratio
      } else if (country === 'MALAYSIA') {
        targetWidth = 350;
        targetHeight = 500; // 3.5:5 aspect ratio
      }

      const targetAspect = targetWidth / targetHeight;
      let srcWidth = img.width;
      let srcHeight = img.height;
      let startX = 0;
      let startY = 0;

      if (img.width / img.height > targetAspect) {
        srcWidth = img.height * targetAspect;
        startX = (img.width - srcWidth) / 2;
      } else {
        srcHeight = img.width / targetAspect;
        startY = (img.height - srcHeight) / 2;
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, startX, startY, srcWidth, srcHeight, 0, 0, targetWidth, targetHeight);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
        setCroppedImage(dataUrl);

        const compressedKB = Math.round((dataUrl.length * 3) / 4 / 1024);
        setFileInfo(
          `✅ Cropped & Compressed to ${targetWidth}x${targetHeight}px (${country} Standard, ~${compressedKB} KB)`
        );
      }
    };
    img.src = selectedImage;
  };

  const getSubTitleText = () => {
    if (country === 'VIETNAM') {
      return 'Vietnam Govt requires a newly taken 4x6 cm photo (JPG/JPEG format, size < 2MB, straight face, no hat, no glasses, formal clothes, white background). Only upload 1 clear passport data page with no lost corners.';
    }
    if (country === 'MALAYSIA') {
      return 'Malaysia Govt requires a 35x50 mm photo (plain white background, JPEG format, size < 1MB).';
    }
    return 'Indian Govt requires an exact 1:1 square photo (equal width & height, JPEG, 10KB - 1MB).';
  };

  const getCropButtonText = () => {
    if (country === 'VIETNAM') return '✂️ Crop to 4:6 Standard (400x600)';
    if (country === 'MALAYSIA') return '✂️ Crop to 35x50 Standard (350x500)';
    return '✂️ Crop to 1:1 Square (350x350)';
  };

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #cbd5e1',
        borderRadius: '12px',
        padding: '1.25rem',
        marginTop: '1.25rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.25rem' }}>✂️</span>
        <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
          Interactive Passport Photo Cropper & Resizer ({country})
        </h4>
      </div>
      <p style={{ fontSize: '0.825rem', color: '#475569', marginBottom: '0.75rem', lineHeight: '1.4' }}>
        {getSubTitleText()}
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
        <label
          style={{
            background: '#0284c7',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          📷 Select Photo to Crop
          <input type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleImageSelect} style={{ display: 'none' }} />
        </label>

        {selectedImage && (
          <button
            type="button"
            onClick={handleCropImage}
            className="btn-primary"
            style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
          >
            {getCropButtonText()}
          </button>
        )}
      </div>

      {fileInfo && (
        <div style={{ fontSize: '0.8rem', color: '#0369a1', fontWeight: '600', marginBottom: '0.5rem' }}>
          {fileInfo}
        </div>
      )}

      {croppedImage && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#15803d', marginBottom: '0.35rem' }}>
            Preview Cropped Photo:
          </div>
          <img
            src={croppedImage}
            alt="Cropped Passport Preview"
            style={{
              maxHeight: '220px',
              borderRadius: '8px',
              border: '2px solid #22c55e',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            }}
          />
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};
